import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { TierEarnedView, TierPipView } from '../../models/achievement.model';

interface ConfettiParticle {
  x: number; y: number; vx: number; vy: number;
  w: number; h: number; rot: number; vr: number;
  color: string; shape: 'rect' | 'circle' | 'strip';
  life: number; ttl: number; wob: number;
}

/**
 * Tier-earned celebration. Centred modal on desktop, bottom sheet on mobile —
 * same chrome rules as LadderDetailSheetComponent. Confetti is canvas-drawn,
 * physics and burst schedule ported from the Tier Unlock Modal reference.
 */
@Component({
  selector: 'app-tier-earned-sheet',
  templateUrl: './tier-earned-sheet.component.html',
  styleUrls: ['./tier-earned-sheet.component.scss']
})
export class TierEarnedSheetComponent implements AfterViewInit, OnDestroy {
  @Input() earned: TierEarnedView;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('confettiCanvas') private canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('badge') private badgeRef: ElementRef<HTMLElement>;
  @ViewChild('panel') private panelRef: ElementRef<HTMLElement>;
  @ViewChild('scrim') private scrimRef: ElementRef<HTMLElement>;

  // ponytail: hardcoded palette like the reference; swap for computed tier tokens if brands need it
  private readonly colors = ['#d9a520', '#f3d774', '#fffbea', '#ffffff', '#2f9e5e', '#b87333', '#c9ced6'];
  private particles: ConfettiParticle[] = [];
  private raf: number | null = null;
  private timers: number[] = [];

  private drag: { startY: number; startedAt: number; dy: number } | null = null;
  private dismissing = false;

  constructor(private readonly ngZone: NgZone) { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => this.bindDrag());

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Three bursts land inside the top-half stagger: badge pop, title, subtitle.
    // Each fires a haptic tap sized to its power.
    const bursts: [number, number, number][] = [[340, 90, 1.0], [640, 40, 0.7], [760, 27, 0.55]];
    this.ngZone.runOutsideAngular(() => {
      for (const [at, count, power] of bursts) {
        this.timers.push(window.setTimeout(() => {
          this.burst(count, power);
          this.haptic(Math.round(40 * power));
        }, at));
      }
    });
  }

  ngOnDestroy(): void {
    this.timers.forEach(clearTimeout);
    if (this.raf) {
      cancelAnimationFrame(this.raf);
    }
  }

  close(): void {
    this.closed.emit();
  }

  trackByPipKey(index: number, pip: TierPipView): string {
    return pip.key;
  }

  /* Drag-to-dismiss (bottom sheet only) ------------------------------------
     Follows the finger 1:1 downward, with friction upward. Dismisses on either
     distance (>35% of the sheet) or a quick flick, so a short fast swipe works.
     Scrim fades with the drag so the gesture reads as one connected motion. */

  private bindDrag(): void {
    const panel = this.panelRef.nativeElement;
    panel.addEventListener('pointerdown', this.onDragStart);
    panel.addEventListener('pointermove', this.onDragMove);
    panel.addEventListener('pointerup', this.onDragEnd);
    panel.addEventListener('pointercancel', this.onDragEnd);
  }

  private onDragStart = (event: PointerEvent): void => {
    if (this.dismissing || !window.matchMedia('(max-width: 639px)').matches) {
      return;
    }
    // Pointer capture would redirect the click away from the CTAs.
    if ((event.target as HTMLElement).closest('button, a')) {
      return;
    }
    const panel = this.panelRef.nativeElement;
    // Kill the entrance animations so inline transforms/opacity win the cascade.
    panel.style.animation = 'none';
    panel.style.transition = 'none';
    this.scrimRef.nativeElement.style.animation = 'none';
    this.scrimRef.nativeElement.style.transition = 'none';
    panel.setPointerCapture(event.pointerId);
    this.drag = { startY: event.clientY, startedAt: performance.now(), dy: 0 };
  };

  private onDragMove = (event: PointerEvent): void => {
    if (!this.drag) {
      return;
    }
    const raw = event.clientY - this.drag.startY;
    // Upward drag meets increasing resistance instead of a hard stop.
    this.drag.dy = raw >= 0 ? raw : -Math.sqrt(-raw) * 2;
    const panel = this.panelRef.nativeElement;
    panel.style.transform = `translateY(${this.drag.dy}px)`;
    this.scrimRef.nativeElement.style.opacity = String(1 - Math.max(0, this.drag.dy) / panel.offsetHeight);
  };

  private onDragEnd = (): void => {
    if (!this.drag) {
      return;
    }
    const { dy, startedAt } = this.drag;
    this.drag = null;
    const panel = this.panelRef.nativeElement;
    const velocity = dy / (performance.now() - startedAt); // px per ms
    const shouldDismiss = dy > panel.offsetHeight * 0.35 || velocity > 0.5;

    // Release is always snappy: fast exit, or an ease-out spring back.
    panel.style.transition = 'transform 250ms cubic-bezier(0.32, 0.72, 0, 1)';
    this.scrimRef.nativeElement.style.transition = 'opacity 250ms ease-out';

    if (shouldDismiss) {
      this.dismissing = true;
      panel.style.transform = 'translateY(100%)';
      this.scrimRef.nativeElement.style.opacity = '0';
      this.timers.push(window.setTimeout(() => this.ngZone.run(() => this.close()), 250));
    } else {
      panel.style.transform = 'translateY(0)';
      this.scrimRef.nativeElement.style.opacity = '1';
    }
  };

  /**
   * Vibration API where it exists (Android). Every iPhone browser is WebKit and lacks it,
   * so fall back to toggling a hidden `<input type="checkbox" switch>`, which iOS 18+ answers
   * with a single fixed system tap. Undocumented, so it is wrapped and may silently stop working.
   */
  private haptic(durationMs: number): void {
    if (!window.matchMedia('(pointer: coarse)').matches) {
      return;
    }
    if ('vibrate' in navigator) {
      navigator.vibrate(durationMs);
      return;
    }
    try {
      const label = document.createElement('label');
      label.setAttribute('aria-hidden', 'true');
      label.style.display = 'none';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.setAttribute('switch', '');
      label.appendChild(input);
      document.head.appendChild(label);
      label.click();
      document.head.removeChild(label);
    } catch {
      // ponytail: best-effort haptic, nothing to recover
    }
  }

  private burst(count: number, power: number): void {
    const badge = this.badgeRef?.nativeElement.getBoundingClientRect();
    if (!badge) {
      return;
    }
    const cx = badge.left + badge.width / 2;
    const cy = badge.top + badge.height / 2;
    const shapes: ConfettiParticle['shape'][] = ['rect', 'circle', 'strip'];

    for (let i = 0; i < count; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.4;
      const sp = (6 + Math.random() * 9) * power;
      this.particles.push({
        x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2,
        w: 5 + Math.random() * 5, h: 3 + Math.random() * 6,
        rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35,
        color: this.colors[Math.floor(Math.random() * this.colors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        life: 0, ttl: 90 + Math.random() * 60, wob: Math.random() * Math.PI * 2
      });
    }
    if (!this.raf) {
      this.tick();
    }
  }

  private tick = (): void => {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.particles = this.particles.filter(p => p.life < p.ttl && p.y < canvas.height + 20);

    for (const p of this.particles) {
      p.life++;
      p.vy += 0.22;
      p.vx *= 0.985;
      p.vy *= 0.985;
      p.x += p.vx + Math.sin(p.wob + p.life * 0.15) * 0.6;
      p.y += p.vy;
      p.rot += p.vr;

      ctx.save();
      ctx.globalAlpha = Math.min(1, (p.ttl - p.life) / 25);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.w / 2.2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 'strip') {
        ctx.fillRect(-p.w, -1.2, p.w * 2, 2.4);
      } else {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.life * 0.12)));
      }
      ctx.restore();
    }

    this.raf = this.particles.length ? requestAnimationFrame(this.tick) : null;
  };
}
